/**
 * C-12 スタッフと権限（FR-02・FR-115・T-D23）。
 *
 * **なぜ M1 に要るか**：これが無いと卸は全員が1つの admin アカウントを共用するしかなく、
 * 「誰が承認したか」が記録に残らない（森井氏の決定①＝複数担当者が前提）。
 * ログイン画面の「管理者に再設定を依頼してください」の行き先もここ。
 *
 * 認可（03 §7）：**参照は staff も可・変更は admin のみ**。
 * 設定URLは **1回だけ表示**（閉じたら再発行）＋**メールでも送る**（D-81④・M1.5）＝
 * **両方やる**。メールを唯一の経路にすると、迷惑メール判定・アドレス誤入力・共有アドレスの
 * ときに卸の担当者が自力で復旧できなくなる。
 */

import { auth, console_, type Staff as StaffRow } from "@kenzai/api-client";
import { Badge, Card, ConfirmDialog, Empty, ErrorNote, PageHeader, Spinner, Table, Td } from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { hintStyle, inputStyle, labelStyle, overlayStyle } from "./formStyles";

const ROLE_LABEL: Record<StaffRow["role"], string> = { admin: "管理者", staff: "担当者" };
const STATUS_LABEL: Record<StaffRow["status"], string> = {
  invited: "招待中",
  active: "利用中",
  disabled: "無効",
};

/**
 * U9③：**ステータスを先頭**に置く（この表で最初に知りたいのは「その人はいま使えるのか」）。
 * 呼び名も「状態」→「ステータス」に統一＝他画面のバッジ列と同じ語彙にする。
 */
const STAFF_TABLE_HEAD = ["ステータス", "お名前", "メールアドレス", "権限", ""];

export function Staff() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["staff"], queryFn: () => console_.staff() });
  const me = useQuery({ queryKey: ["me"], queryFn: () => auth.me(), staleTime: Infinity });
  const isAdmin = me.data?.role === "admin";

  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState<{ name: string; url: string; emailSent: boolean } | null>(null);
  const [disabling, setDisabling] = useState<StaffRow | null>(null);

  /** 設定URLの再発行（FR-112②・03 §4.1 の②管理経路＝**M1 の唯一の再設定経路**）。
   *  これが無かったので「招待URLを閉じたら、同じメールでは再招待もできない」行き止まりだった。 */
  const reissue = useMutation({
    // 再発行も既定でメールを送る（D-81④）。URL は必ず併せて表示する。
    mutationFn: (row: StaffRow) => console_.reissueStaffSetupLink(row.id, true),
    onSuccess: (res) => {
      invalidate();
      setIssued({
        name: res.user.name,
        url: `${window.location.origin}${res.setup_path}`,
        emailSent: res.email_sent,
      });
    },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["staff"] });

  // status は API 側が active/disabled しか受けない（invited は招待の結果であって
  // 手で設定する状態ではない＝「パスワード未設定なのに利用中」を作らせない）。
  type StaffPatchBody = { role?: StaffRow["role"]; status?: "active" | "disabled" };

  const patch = useMutation({
    mutationFn: ({ row, body }: { row: StaffRow; body: StaffPatchBody }) =>
      console_.patchStaff(row.id, { role: body.role, status: body.status, updated_at: row.updated_at }),
    onSuccess: () => {
      invalidate();
      setDisabling(null);
    },
  });

  if (list.isLoading || me.isLoading) return <Spinner />;
  if (list.isError) return <ErrorNote error={list.error} />;

  const rows = list.data ?? [];

  return (
    <div>
      <PageHeader
        title="スタッフと権限"
        actions={
          isAdmin ? (
            <button className="kz-btn kz-btn--primary" onClick={() => setInviting(true)} data-testid="staff-invite-open">
              スタッフを招待
            </button>
          ) : undefined
        }
      />

      <p style={{ ...hintStyle, marginBottom: "var(--kz-space-4)" }}>
        担当者ごとにアカウントを分けると、受注の承認や修正が「誰の操作か」として記録に残ります。
        {!isAdmin && "（追加・変更は管理者のみ行えます）"}
      </p>

      {(patch.isError || reissue.isError) && <ErrorNote error={patch.error ?? reissue.error} />}

      <Card>
        {rows.length === 0 ? (
          <Empty title="まだスタッフがいません。" hint="「スタッフを招待」から追加できます。" />
        ) : (
          <Table head={STAFF_TABLE_HEAD}>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`staff-row-${r.id}`}>
                <Td>
                  <span data-testid={`staff-status-${r.id}`}>
                    <Badge tone={r.status === "active" ? "ok" : r.status === "invited" ? "warn" : "neutral"}>
                      {STATUS_LABEL[r.status]}
                    </Badge>
                  </span>
                </Td>
                <Td>{r.name}</Td>
                <Td>{r.email}</Td>
                <Td>
                  {isAdmin ? (
                    <select className="kz-select"
                      style={{ ...inputStyle, width: "auto", margin: 0 }}
                      value={r.role}
                      disabled={patch.isPending}
                      data-testid={`staff-role-${r.id}`}
                      onChange={(e) =>
                        patch.mutate({ row: r, body: { role: e.target.value as StaffRow["role"] } })
                      }
                    >
                      <option value="staff">担当者</option>
                      <option value="admin">管理者</option>
                    </select>
                  ) : (
                    ROLE_LABEL[r.role]
                  )}
                </Td>
                <Td>
                  {/* U9②：ボタンを**離す**（gap 無しで直列に置くと隙間ゼロでくっつく＝
                      「設定URLを再発行」と「無効にする」を押し間違える） */}
                  <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
                  {isAdmin && r.status !== "disabled" && (
                    <button
                      className="kz-btn kz-btn--sm"
                      disabled={reissue.isPending}
                      onClick={() => reissue.mutate(r)}
                      data-testid={`staff-reset-link-${r.id}`}
                    >
                      設定URLを再発行
                    </button>
                  )}
                  {isAdmin && r.status !== "disabled" && (
                    <button
                      className="kz-btn kz-btn--sm"
                      onClick={() => setDisabling(r)}
                      data-testid={`staff-disable-${r.id}`}
                    >
                      無効にする
                    </button>
                  )}
                  {isAdmin && r.status === "disabled" && (
                    <button
                      className="kz-btn kz-btn--sm"
                      disabled={patch.isPending}
                      onClick={() => patch.mutate({ row: r, body: { status: "active" } })}
                      data-testid={`staff-enable-${r.id}`}
                    >
                      元に戻す
                    </button>
                  )}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {inviting && (
        <InviteDialog
          onClose={() => setInviting(false)}
          onIssued={(v) => {
            setInviting(false);
            setIssued(v);
            invalidate();
          }}
        />
      )}

      {issued && <IssuedDialog {...issued} onClose={() => setIssued(null)} />}

      {disabling && (
        <ConfirmDialog
          title={`${disabling.name} さんを無効にしますか？`}
          body={
            <>
              すぐにログインできなくなり、開いている画面も使えなくなります。これまでの操作の記録（誰が承認したか）は残ります。
              {/* ★D-69：宛先の変更 API は作らない（FR-121＝取り消して出し直すのが正）ので、
                  この人あての未完了の指示が残っていると**誰も進められないまま滞留する**。
                  件数を出して「指示・依頼」画面での取消・出し直しへ誘導する。
                  リンクはダイアログの中に置かない（押すと確認中の無効化が黙って捨てられる）。 */}
              {disabling.open_request_count > 0 && (
                <>
                  <br />
                  <strong data-testid="staff-open-requests-warning">
                    この方あての未完了の指示・依頼が {disabling.open_request_count} 件あります。
                  </strong>
                  <br />
                  無効にしたあと、「指示・依頼」の画面で取り消して別の方に出し直してください。
                </>
              )}
            </>
          }
          confirmLabel="無効にする"
          tone="danger"
          busy={patch.isPending}
          onCancel={() => setDisabling(null)}
          onConfirm={() => patch.mutate({ row: disabling, body: { status: "disabled" } })}
        />
      )}
    </div>
  );
}

function InviteDialog({
  onClose,
  onIssued,
}: {
  onClose: () => void;
  onIssued: (v: { name: string; url: string; emailSent: boolean }) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<StaffRow["role"]>("staff");
  // D-81④：既定 ON。切れるようにしてあるのは、共有アドレス宛など
  // 「送らずに口頭で渡したい」ケースが実運用にあるため。
  const [sendEmail, setSendEmail] = useState(true);

  const invite = useMutation({
    mutationFn: () =>
      console_.inviteStaff({
        email: email.trim(),
        name: name.trim(),
        role,
        send_email: sendEmail,
      }),
    onSuccess: (res) =>
      onIssued({
        name: res.user.name,
        // **パスは backend が返す**（`/password/setup?token=...`）＝ここで組み立てない。
        // 本人が別の端末で開くので絶対URLにする。
        url: `${window.location.origin}${res.setup_path}`,
        emailSent: res.email_sent,
      }),
  });

  return (
    <div style={overlayStyle}>
      <Card>
        <h2 style={{ marginTop: 0 }}>スタッフを招待</h2>
        {invite.isError && <ErrorNote error={invite.error} />}
        {/* U8：**フィールドを離す**＝labelStyle は自分の中の gap しか持たないので、
            ラッパーが無いと入力欄が隙間ゼロで並ぶ（他のモーダルは gap ラッパーを持っている） */}
        <div style={{ display: "grid", gap: "var(--kz-space-3)", marginTop: "var(--kz-space-4)" }}>
        <label style={labelStyle}>
          お名前
          <input
            style={inputStyle}
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            data-testid="staff-name"
          />
        </label>
        <label style={labelStyle}>
          メールアドレス
          <input
            style={inputStyle}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="staff-email"
          />
        </label>
        <label style={labelStyle}>
          権限
          <select className="kz-select"
            style={inputStyle}
            value={role}
            onChange={(e) => setRole(e.target.value as StaffRow["role"])}
            data-testid="staff-invite-role"
          >
            <option value="staff">担当者（受注の入力・承認）</option>
            <option value="admin">管理者（スタッフやマスタの管理も）</option>
          </select>
        </label>
        <label
          style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-2)", cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            data-testid="staff-send-email"
          />
          設定用URLをメールで送る
        </label>
        </div>
        <p style={hintStyle}>
          メールを送っても、次の画面に<strong>設定用URL</strong>が出ます。
          届かないときは、そのURLをご本人にお伝えください。
        </p>
        <div style={{ display: "flex", gap: "var(--kz-space-2)", marginTop: "var(--kz-space-4)" }}>
          <button
            className="kz-btn kz-btn--primary"
            disabled={invite.isPending || !email.trim() || !name.trim()}
            onClick={() => invite.mutate()}
            data-testid="staff-invite-submit"
          >
            {invite.isPending ? "招待しています…" : "招待する"}
          </button>
          <button className="kz-btn" onClick={onClose}>
            やめる
          </button>
        </div>
      </Card>
    </div>
  );
}

/** 設定URLは**1回だけ**表示する（DB にはハッシュしか無く、閉じたら二度と出せない）。 */
function IssuedDialog({
  name,
  url,
  emailSent,
  onClose,
}: {
  name: string;
  url: string;
  emailSent: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={overlayStyle}>
      <Card>
        <h2 style={{ marginTop: 0 }}>{name} さんの設定用URL</h2>
        {/* ★**送っていないのに「送りました」と言わない**（D-81④）＝送信基盤が未設定でも、
            プロバイダに拒否されても false になる。ここで嘘をつくと、卸は届かないメールを
            待ち続けて開通が止まる。 */}
        <p data-testid="staff-issued-mail-state">
          {emailSent
            ? "設定用URLをメールでお送りしました。届かない場合は、下のURLをお伝えください。"
            : "メールは送られていません。下のURLをご本人にお伝えください。"}
        </p>
        <p>
          このURLは<strong>この画面を閉じると二度と表示できません</strong>。
          ご本人に伝えてから閉じてください（伝え忘れたら、一覧の「設定URLを再発行」からやり直せます）。
        </p>
        <code
          data-testid="staff-setup-url"
          style={{
            display: "block",
            padding: "var(--kz-space-3)",
            background: "var(--kz-color-surface-sunken)",
            borderRadius: "var(--kz-radius-sm)",
            wordBreak: "break-all",
            fontSize: "var(--kz-font-size-sm)",
          }}
        >
          {url}
        </code>
        <div style={{ display: "flex", gap: "var(--kz-space-2)", marginTop: "var(--kz-space-4)" }}>
          <button
            className="kz-btn kz-btn--primary"
            onClick={() => {
              void navigator.clipboard?.writeText(url).then(() => setCopied(true));
            }}
          >
            {copied ? "コピーしました" : "URLをコピー"}
          </button>
          <button className="kz-btn" onClick={onClose} data-testid="staff-issued-close">
            閉じる
          </button>
        </div>
      </Card>
    </div>
  );
}
